/**
 * section-main-product.js
 * Handles: gallery navigation, pagination, sticky ATC, Ajax ATC, USP drawer
 */

class MainProduct {
    constructor() {
      this.container = document.querySelector('.js-product-section');
      if (!this.container) return;
  
      this.form            = this.container.querySelector('.js-product-form');
      this.errorEl         = this.container.querySelector('.js-atc-errors');
      this.stickyErrorEl   = this.container.querySelector('.js-sticky-atc-errors');
      this._errorTimer     = null;
  
      this.init();
    }
  
    /* ------------------------------------------------------------------
       Error display
    ------------------------------------------------------------------ */
    showError(msg) {
      [this.errorEl, this.stickyErrorEl].forEach((el) => {
        if (!el) return;
        el.textContent = msg;
        el.style.display = 'block';
      });
      clearTimeout(this._errorTimer);
      this._errorTimer = setTimeout(() => {
        [this.errorEl, this.stickyErrorEl].forEach((el) => {
          if (el) el.style.display = 'none';
        });
      }, 5000);
    }
  
    /* ------------------------------------------------------------------
       Inventory check
    ------------------------------------------------------------------ */
    canAddUnit(qtyInCart, inventoryQty, policy, management) {
      if (management !== 'shopify') return true;
      if (policy === 'continue') return true;
      const max = parseInt(inventoryQty, 10);
      return isNaN(max) ? true : qtyInCart + 1 <= max;
    }
  
    /* ------------------------------------------------------------------
       Gallery
    ------------------------------------------------------------------ */
    initGallery() {
      const track = this.container.querySelector('.js-media-track');
      const dots  = this.container.querySelectorAll('.js-pagination .pagination-dot');
      if (!track) return;
  
      const getStep = () => window.innerWidth >= 990 ? track.offsetWidth / 2 : track.offsetWidth;
      const scrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth';
  
      const updateDots = () => {
        const step  = getStep();
        const index = Math.round(track.scrollLeft / (step || 1));
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
          dot.setAttribute('aria-selected', String(i === index));
        });
      };
  
      track.addEventListener('scroll', updateDots, { passive: true });
  
      // Arrow navigation
      this.container.querySelector('.js-prev')?.addEventListener('click', () => {
        track.scrollBy({ left: -getStep(), behavior: scrollBehavior });
      });
      this.container.querySelector('.js-next')?.addEventListener('click', () => {
        track.scrollBy({ left: getStep(), behavior: scrollBehavior });
      });
  
      // Dot click navigation
      dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          track.scrollTo({ left: getStep() * i, behavior: scrollBehavior });
        });
      });
    }
  
    /* ------------------------------------------------------------------
       Sibling / variant navigation
    ------------------------------------------------------------------ */
    initVariants() {
      this.container.addEventListener('click', (e) => {
        const swatch = e.target.closest('.swatch-item');
        if (!swatch) return;
        e.preventDefault();
        const url = swatch.getAttribute('href');
        if (url) {
          this.container.style.opacity = '0.5';
          window.location.href = url;
        }
      });
    }

    initMediaPlayback() {
      const videos = this.container.querySelectorAll('.product-video');
      if (!videos.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) return;
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }, { rootMargin: '200px 0px', threshold: 0.1 });

      videos.forEach((video) => observer.observe(video));
    }
  
    /* ------------------------------------------------------------------
       Sticky ATC
    ------------------------------------------------------------------ */
    initStickyATC() {
      const mainBtn   = this.container.querySelector('.js-atc-button');
      const stickyBar = this.container.querySelector('.js-sticky-atc');
      const stickyBtn = this.container.querySelector('.js-sticky-submit');
      if (!mainBtn || !stickyBar) return;
  
      // Show/hide sticky bar based on main button visibility
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const belowFold = !entry.isIntersecting && window.scrollY > entry.boundingClientRect.top + window.scrollY;
          stickyBar.classList.toggle('sticky-atc--active', belowFold);
          stickyBar.setAttribute('aria-hidden', String(!belowFold));
        });
      }, { threshold: 0 });
      observer.observe(mainBtn);
  
      // Clone sticky button to avoid duplicate event listeners
      if (stickyBtn) {
        const clone = stickyBtn.cloneNode(true);
        stickyBtn.parentNode.replaceChild(clone, stickyBtn);
        clone.addEventListener('click', () => {
          this.form?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        });
      }
    }
  
    /* ------------------------------------------------------------------
       Ajax ATC
    ------------------------------------------------------------------ */
    async handleAdd(e) {
      e.preventDefault();
      if (!this.form) return;
  
      const mainBtn   = this.form.querySelector('.js-atc-button');
      const stickyBtn = this.container.querySelector('.js-sticky-submit');
  
      if (mainBtn?.disabled) return;
  
      const setLoading = (on) => {
        [mainBtn, stickyBtn].forEach((btn) => {
          if (!btn) return;
          btn.classList.toggle('is-loading', on);
          btn.disabled = on;
        });
      };
  
      const formData  = new FormData(this.form);
      formData.set('sections', 'wyms-cart-drawer');
      formData.set('sections_url', window.location.pathname);
      setLoading(true);
  
      try {
        const addRes = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
        });
  
        if (!addRes.ok) {
          const result = await addRes.json();
          this.showError(result.description || 'Error adding to cart.');
          return;
        }

        const result = await addRes.json();
        const sectionHTML = result.sections?.['wyms-cart-drawer'];
        if (window.wymsCart && typeof window.wymsCart.refreshAndOpen === 'function') {
          await window.wymsCart.refreshAndOpen(sectionHTML);
        } else {
          document.dispatchEvent(new CustomEvent('cart:open'));
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      } catch (err) {
        console.error(err);
        this.showError('Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  
    initAjaxATC() {
      this.form?.addEventListener('submit', (e) => this.handleAdd(e));
    }
  
    /* ------------------------------------------------------------------
       Init
    ------------------------------------------------------------------ */
    init() {
      this.initGallery();
      this.initVariants();
      this.initMediaPlayback();
      this.initStickyATC();
      this.initAjaxATC();
    }
  }
  
  
  class USPDrawer {
    constructor() {
      this.overlay   = document.querySelector('.js-usp-overlay');
      this.drawer    = document.querySelector('.js-usp-drawer');
      this.titleEl   = document.querySelector('.js-usp-title');
      this.descEl    = document.querySelector('.js-usp-desc');
      this.iconEl    = document.querySelector('.js-usp-icon');
      this.imageEl   = document.querySelector('.js-usp-image');

      if (!this.drawer) return;
      this.isOpen = false;
      this._slideIndex = 0;
      this._slideCount = 0;
      this._isAnimating = false;
      this._init();
    }

    open(data) {
      if (this.titleEl)  this.titleEl.textContent  = data.title || '';
      if (this.descEl)   this.descEl.innerHTML      = data.description || '';

      if (this.iconEl) {
        if (data.icon) {
          this.iconEl.src          = data.icon;
          this.iconEl.style.display = 'block';
        } else {
          this.iconEl.style.display = 'none';
        }
      }

      const images = Array.isArray(data.images)
        ? data.images.filter(Boolean)
        : (data.imgUrl ? [data.imgUrl] : []);
      this._renderImages(images);

      this.isOpen = true;
      this.drawer.classList.add('is-active');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.overlay?.classList.add('is-active');
      this.overlay?.setAttribute('aria-hidden', 'false');
      document.body.classList.add('usp-drawer-open');
      document.body.style.overflow = 'hidden';
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.drawer.classList.remove('is-active');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.overlay?.classList.remove('is-active');
      this.overlay?.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('usp-drawer-open');
      document.body.style.overflow = '';
    }

    /**
     * @param {string[]} urls
     */
    _renderImages(urls) {
      if (!this.imageEl) return;

      this._slideIndex = 0;
      this._slideCount = urls.length;
      this._isAnimating = false;
      this.imageEl.classList.toggle('is-empty', urls.length === 0);

      if (!urls.length) {
        this.imageEl.innerHTML = '';
        return;
      }

      if (urls.length === 1) {
        this.imageEl.innerHTML = `<img src="${urls[0]}" alt="" loading="lazy">`;
        return;
      }

      const slides = urls
        .map(
          (url) =>
            `<div class="usp-drawer__slide">
              <img src="${url}" alt="" loading="lazy" draggable="false">
            </div>`
        )
        .join('');

      const dots = urls
        .map(
          (_, i) =>
            `<button type="button" class="usp-drawer__dot${i === 0 ? ' is-active' : ''}" data-index="${i}" aria-label="Go to image ${i + 1}"></button>`
        )
        .join('');

      this.imageEl.innerHTML = `
        <div class="usp-drawer__slider js-usp-slider">
          <div class="usp-drawer__viewport">
            <div class="usp-drawer__track js-usp-slides" style="transform: translate3d(0, 0, 0);">
              ${slides}
            </div>
          </div>
          <button type="button" class="usp-drawer__nav usp-drawer__nav--prev js-usp-prev" aria-label="Previous image">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M15.8332 9.99984H4.1665M4.1665 9.99984L9.99984 15.8332M4.1665 9.99984L9.99984 4.1665" stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button type="button" class="usp-drawer__nav usp-drawer__nav--next js-usp-next" aria-label="Next image">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M4.16675 9.99984H15.8334M15.8334 9.99984L10.0001 4.1665M15.8334 9.99984L10.0001 15.8332" stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="usp-drawer__dots js-usp-dots">${dots}</div>
        </div>
      `;

      this._bindSlider();
      this._updateNavState();
    }

    _bindSlider() {
      if (!this.imageEl) return;
      const prev = this.imageEl.querySelector('.js-usp-prev');
      const next = this.imageEl.querySelector('.js-usp-next');
      const dots = this.imageEl.querySelectorAll('.js-usp-dots [data-index]');
      const track = /** @type {HTMLElement | null} */ (this.imageEl.querySelector('.js-usp-slides'));
      const viewport = this.imageEl.querySelector('.usp-drawer__viewport');

      prev?.addEventListener('click', (e) => {
        e.stopPropagation();
        this._goToSlide(this._slideIndex - 1);
      });
      next?.addEventListener('click', (e) => {
        e.stopPropagation();
        this._goToSlide(this._slideIndex + 1);
      });
      dots.forEach((dot) => {
        dot.addEventListener('click', (e) => {
          e.stopPropagation();
          const idx = Number(/** @type {HTMLElement} */ (dot).dataset.index);
          if (!Number.isNaN(idx)) this._goToSlide(idx);
        });
      });

      track?.addEventListener('transitionend', (e) => {
        if (e.target !== track || e.propertyName !== 'transform') return;
        this._isAnimating = false;
      });

      // Touch / pointer swipe
      if (viewport instanceof HTMLElement) {
        let startX = 0;
        let deltaX = 0;
        let active = false;

        const onStart = (clientX) => {
          if (this._isAnimating) return;
          active = true;
          startX = clientX;
          deltaX = 0;
          track?.classList.add('is-dragging');
        };

        const onMove = (clientX) => {
          if (!active || !track) return;
          deltaX = clientX - startX;
          const offset = -this._slideIndex * 100 + (deltaX / viewport.offsetWidth) * 100;
          track.style.transform = `translate3d(${offset}%, 0, 0)`;
        };

        const onEnd = () => {
          if (!active) return;
          active = false;
          track?.classList.remove('is-dragging');
          const threshold = (viewport.offsetWidth || 1) * 0.18;
          if (deltaX > threshold) this._goToSlide(this._slideIndex - 1);
          else if (deltaX < -threshold) this._goToSlide(this._slideIndex + 1);
          else this._goToSlide(this._slideIndex);
          deltaX = 0;
        };

        viewport.addEventListener('pointerdown', (e) => {
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          onStart(e.clientX);
          viewport.setPointerCapture?.(e.pointerId);
        });
        viewport.addEventListener('pointermove', (e) => onMove(e.clientX));
        viewport.addEventListener('pointerup', onEnd);
        viewport.addEventListener('pointercancel', onEnd);
      }
    }

    _updateNavState() {
      if (!this.imageEl) return;
      const prev = /** @type {HTMLButtonElement | null} */ (this.imageEl.querySelector('.js-usp-prev'));
      const next = /** @type {HTMLButtonElement | null} */ (this.imageEl.querySelector('.js-usp-next'));
      if (prev) {
        prev.disabled = this._slideIndex <= 0;
        prev.classList.toggle('is-disabled', this._slideIndex <= 0);
      }
      if (next) {
        next.disabled = this._slideIndex >= this._slideCount - 1;
        next.classList.toggle('is-disabled', this._slideIndex >= this._slideCount - 1);
      }
    }

    /** @param {number} index */
    _goToSlide(index) {
      if (!this.imageEl || this._slideCount < 2) return;

      const nextIndex = Math.max(0, Math.min(this._slideCount - 1, index));
      const track = /** @type {HTMLElement | null} */ (this.imageEl.querySelector('.js-usp-slides'));
      if (!track) return;

      if (nextIndex !== this._slideIndex) this._isAnimating = true;
      this._slideIndex = nextIndex;
      track.style.transform = `translate3d(-${nextIndex * 100}%, 0, 0)`;

      this.imageEl.querySelectorAll('.usp-drawer__dot').forEach((dot, i) => {
        dot.classList.toggle('is-active', i === nextIndex);
      });
      this._updateNavState();
    }

    _init() {
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('.js-usp-trigger');
        if (trigger) {
          e.preventDefault();
          const rawImages = trigger.dataset.uspImages || trigger.dataset.uspImage || '';
          const images = rawImages
            .split('|')
            .map((s) => s.trim())
            .filter(Boolean);

          this.open({
            title:       trigger.dataset.uspTitle,
            description: trigger.dataset.uspDescription,
            icon:        trigger.dataset.uspIcon,
            images,
          });
          return;
        }

        if (e.target.closest('.js-usp-close') || e.target === this.overlay) {
          this.close();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (!this.isOpen) return;
        if (e.key === 'Escape') {
          this.close();
          return;
        }
        if (e.key === 'ArrowLeft') this._goToSlide(this._slideIndex - 1);
        if (e.key === 'ArrowRight') this._goToSlide(this._slideIndex + 1);
      });
    }
  }
  
  class ProductLightbox {
    constructor() {
      this.section = document.querySelector('.js-product-section');
      if (!this.section) return;

      this.lightbox = document.querySelector('.js-lightbox');
      this.lightboxImg = this.lightbox?.querySelector('.js-lightbox-img');
      this.counter = this.lightbox?.querySelector('.js-lightbox-counter');
      this.content = this.lightbox?.querySelector('.js-lightbox-content');
      this.imageContainer = this.lightbox?.querySelector('.js-lightbox-bg');

      // Only product media (.js-lightbox-trigger) — not USP icons or other imgs inside .media-item
      this.triggers = Array.from(this.section.querySelectorAll('.js-lightbox-trigger'));
      this.currentIndex = 0;

      this.init();
    }

    init() {
      this.triggers.forEach((img, index) => {
        img.addEventListener('click', (e) => {
          if (e.target.closest('.js-usp-trigger')) return;
          this.currentIndex = index;
          this.open();
        });
      });

      this.lightbox?.querySelector('.js-lightbox-prev')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.changeStep(-1);
      });

      this.lightbox?.querySelector('.js-lightbox-next')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.changeStep(1);
      });

      this.imageContainer?.addEventListener('click', (e) => {
        e.stopPropagation();
        const media = this.imageContainer.querySelector('.lightbox-img');
        if (!media || media.classList.contains('is-video')) return;
        this.toggleZoom(e, media);
      });

      this.lightbox?.querySelector('.js-lightbox-close')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.close();
      });

      document.addEventListener('keydown', (e) => {
        if (!this.lightbox?.classList.contains('is-active')) return;
        if (e.key === 'Escape') this.close();
        if (e.key === 'ArrowLeft') this.changeStep(-1);
        if (e.key === 'ArrowRight') this.changeStep(1);
      });

      this._touchStartX = 0;

      this._onTouchStart = (e) => {
        if (!this.lightbox?.classList.contains('is-active')) return;
        if (this.lightbox.classList.contains('is-zoomed')) return;
        const touch = e.touches[0];
        if (!touch) return;
        this._touchStartX = touch.clientX;
      };

      this._onTouchEnd = (e) => {
        if (!this.lightbox?.classList.contains('is-active')) return;
        if (this.lightbox.classList.contains('is-zoomed')) return;
        const touch = e.changedTouches[0];
        if (!touch) return;
        const deltaX = touch.clientX - this._touchStartX;
        if (deltaX < -50) this.changeStep(1);
        else if (deltaX > 50) this.changeStep(-1);
      };

      this.content?.addEventListener('touchstart', this._onTouchStart, { passive: true });
      this.content?.addEventListener('touchend', this._onTouchEnd, { passive: true });
    }

    /**
     * @param {MouseEvent} e
     * @param {HTMLImageElement} img
     */
    toggleZoom(e, img) {
      if (this.lightbox.classList.contains('is-zoomed')) {
        this.resetZoom(img);
        return;
      }

      const rect = img.getBoundingClientRect();
      const width = rect.width || 1;
      const height = rect.height || 1;
      const originX = Math.min(100, Math.max(0, ((e.clientX - rect.left) / width) * 100));
      const originY = Math.min(100, Math.max(0, ((e.clientY - rect.top) / height) * 100));

      img.style.transformOrigin = `${originX}% ${originY}%`;
      this.lightbox.classList.add('is-zoomed');
    }

    /**
     * @param {HTMLImageElement | null} [img]
     */
    resetZoom(img) {
      const target = img || this.imageContainer?.querySelector('.lightbox-img:not(.is-video)');
      if (target instanceof HTMLImageElement) {
        target.style.transformOrigin = 'center center';
      }
      this.lightbox.classList.remove('is-zoomed');
      this.resetScroll();
    }

    resetScroll() {
      if (!this.content) return;
      this.content.scrollTop = 0;
      this.content.scrollLeft = 0;
    }

    open() {
      this.updateImage();
      this.lightbox.classList.add('is-active');
      this.lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('lightbox-open');
    }

    changeStep(direction) {
      this.resetZoom();
      this.currentIndex += direction;

      if (this.currentIndex >= this.triggers.length) this.currentIndex = 0;
      if (this.currentIndex < 0) this.currentIndex = this.triggers.length - 1;

      this.updateImage();
    }

    updateImage() {
      const mediaElement = this.triggers[this.currentIndex];
      const container = this.imageContainer;

      container.innerHTML = '';
      this.lightbox.classList.remove('is-zoomed');

      if (mediaElement.tagName === 'VIDEO') {
        const video = document.createElement('video');
        video.src = mediaElement.querySelector('source')?.src || mediaElement.src;
        video.autoplay = true;
        video.loop = true;
        video.muted = true;
        video.className = 'lightbox-img is-video js-lightbox-img';
        container.appendChild(video);
        this.lightboxImg = video;
      } else {
        const img = document.createElement('img');
        const highResSrc = mediaElement.src.split('?')[0] + '?width=2000';
        img.src = highResSrc;
        img.alt = mediaElement.alt || '';
        img.className = 'lightbox-img js-lightbox-img';
        container.appendChild(img);
        this.lightboxImg = img;
      }

      if (this.counter) {
        this.counter.textContent = `${this.currentIndex + 1} / ${this.triggers.length}`;
      }
    }

    close() {
      this.resetZoom();
      this.lightbox.classList.remove('is-active');
      this.lightbox.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('lightbox-open');
    }
  }
  
  class GuaranteeModal {
    constructor() {
      this.openers = document.querySelectorAll('.js-guarantee-open');
      this.modals = document.querySelectorAll('.js-guarantee-modal');
      if (!this.openers.length || !this.modals.length) return;

      this.modal = null;
      this.lastFocus = null;
      this._onKeyDown = (event) => {
        if (event.key === 'Escape') this.close();
      };

      this.openers.forEach((opener) => {
        opener.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(opener);
        });
        opener.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.open(opener);
        });
      });

      this.modals.forEach((modal) => {
        modal.querySelector('.js-guarantee-overlay')?.addEventListener('click', () => this.close());
        modal.querySelector('.js-guarantee-close')?.addEventListener('click', () => this.close());
      });
    }

    open(opener) {
      const modal = document.getElementById(opener.getAttribute('aria-controls'));
      if (!modal) return;

      this.modal = modal;
      this.lastFocus = opener;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('guarantee-open');
      document.addEventListener('keydown', this._onKeyDown);
      modal.querySelector('.js-guarantee-close')?.focus();
    }

    close() {
      if (!this.modal) return;
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('guarantee-open');
      document.removeEventListener('keydown', this._onKeyDown);
      this.lastFocus?.focus();
      this.modal = null;
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    const section = document.querySelector('.js-product-section');
    if (!section || section.dataset.wymsInitialized === 'true') return;
    section.dataset.wymsInitialized = 'true';

    new MainProduct();
    new USPDrawer();
    new ProductLightbox();
    new GuaranteeModal();
  });